/**
 * The Briefing page's reader half — API client.
 *
 * Backend: app/routes/reader_memory.py ("What Merlin remembers": the reader's
 * own memory bank, which shapes every watch of theirs) and
 * app/routes/person_digest_chat.py (the profile-LOCKED Briefing chat door at
 * /api/person-digest/chat, with the reader's memory and watches prepended to
 * every turn). The personal digest itself became a watch in kanban 001b6e2b;
 * the watches are services/merlinWatches.ts. The chat half reuses
 * agentConsole.ts's SSE wire contract + types, exactly like analystCopilot.ts.
 */
import api from './api';
import {
  CONSOLE_DOOR_REQUEST_TIMEOUT_MS,
  streamConsoleSse,
  type ConsoleEvent,
  type ConversationCreate,
  type ConversationDetail,
  type ConversationRead,
} from './agentConsole';

export type { ConsoleEvent, ConversationDetail, ConversationRead } from './agentConsole';

/** One thing Merlin remembers about the reader — an entry in their own
 * Knowledge Bank (the agent writes these from chat; the panel adds/removes). */
export interface MemoryEntry {
  id: string;
  text: string;
  source: string;
  created_at: string | null;
}

/** "What Merlin remembers" for the signed-in reader. */
export interface ReaderMemoryView {
  /** False when this login has no reader-memory row yet. */
  bound: boolean;
  display_name: string | null;
  /** False when this environment has no bank for the reader yet, or the
   * platform was unreachable — the page says so instead of showing "none". */
  memory_available: boolean;
  memory: MemoryEntry[];
}

export interface ProvisionResult {
  user_id: string;
  recipient_key: string;
  memory_kb_id: string;
  memory_staging_kb_id: string;
}

const ME = '/reader-memory/me';
const CHAT = '/person-digest/chat';

export const readerMemoryApi = {
  me: async (): Promise<ReaderMemoryView> => (await api.get<ReaderMemoryView>(ME)).data,
  remember: async (text: string): Promise<MemoryEntry> =>
    (await api.post<MemoryEntry>(ME, { text })).data,
  forget: async (id: string): Promise<void> => {
    await api.delete(`${ME}/${id}`);
  },
  /** Admin: create-or-reuse a reader's memory bank AND its staging twin. */
  provision: async (userId: string): Promise<ProvisionResult> =>
    (await api.post<ProvisionResult>(`/admin/reader-memory/${userId}/provision`)).data,
};

// ── chat door (mirrors services/analystCopilot.ts) ───────────────────────────

export const PERSON_DIGEST_CHAT_BASE = CHAT;

export async function createDigestConversation(
  body: ConversationCreate = {},
): Promise<ConversationRead> {
  const { data } = await api.post<ConversationRead>(`${CHAT}/conversations`, body, {
    timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS,
  });
  return data;
}

export async function getDigestConversationDetail(
  conversationId: string,
): Promise<ConversationDetail> {
  const { data } = await api.get<ConversationDetail>(`${CHAT}/conversations/${conversationId}`, {
    timeout: CONSOLE_DOOR_REQUEST_TIMEOUT_MS,
  });
  return data;
}

/**
 * Open the SSE stream for one digest-chat turn. The profile is forced
 * server-side; turn-level failures arrive as `error` frames.
 */
export async function streamDigestMessage(
  conversationId: string,
  prompt: string,
  onEvent: (evt: ConsoleEvent) => void,
  signal?: AbortSignal,
  /** What the reader is looking at while they type (e.g. `{ watch_id }`);
   * the door hands it to Merlin for this turn only, it is never stored. */
  focus?: Record<string, string>,
): Promise<void> {
  const payload: Record<string, unknown> = focus ? { prompt, focus } : { prompt };
  return streamConsoleSse(`/api${CHAT}/conversations/${conversationId}/message`, payload, onEvent, signal);
}
